import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall, apiCallEx, errorText } from "../../apiClient";
import { Btn, Card, Badge, SectionTitle } from "../primitives";

// SharePoint Microsoft-Graph credential fields, backed by the generic config-keys
// API (/api/settings/config-keys) — the SAME mechanism the Email/Graph tab uses,
// with the keys swapped to AZURE_*. The three keys are already registered server
// side in configStore's CONFIGURABLE_KEYS + sensitive set, so the secret is stored
// encrypted (enc:) and the list endpoint never returns it (write-only). With no DB
// value the server falls back to the AZURE_* env vars, so an env-configured
// instance is unchanged; a saved value overrides env.
type SpKeyStatus = { key: string; hasDbValue: boolean; hasEnvValue: boolean; source: "database" | "env" | "not_set"; encrypted: boolean; value?: string };

const SP_CRED_FIELDS: { key: string; label: string; secret?: boolean; help: string }[] = [
  { key: "AZURE_TENANT_ID", label: "SharePoint Tenant ID", help: "Azure AD tenant (directory) ID of the SharePoint app registration." },
  { key: "AZURE_CLIENT_ID", label: "SharePoint Client ID", help: "Application (client) ID of the SharePoint app registration." },
  { key: "AZURE_CLIENT_SECRET", label: "SharePoint Client Secret", secret: true, help: "Client secret value. Write-only — never displayed once saved; stored encrypted." },
];

const SharePointCredentials = () => {
  const [statuses, setStatuses] = useState<Record<string, SpKeyStatus>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [fmsg, setFmsg] = useState<{ key: string; text: string; ok: boolean } | null>(null);

  const load = () => {
    setLoading(true);
    apiCall("/settings/config-keys")
      .then((d: any) => {
        const byKey: Record<string, SpKeyStatus> = {};
        for (const k of (d?.keys || []) as SpKeyStatus[]) byKey[k.key] = k;
        setStatuses(byKey); setDrafts({});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const flash = (key: string, text: string, ok: boolean) => {
    setFmsg({ key, text, ok });
    setTimeout(() => setFmsg(m => (m && m.key === key ? null : m)), 3500);
  };

  const save = async (key: string) => {
    const value = (drafts[key] || "").trim();
    if (!value) { flash(key, "Enter a value first", false); return; }
    setSavingKey(key);
    // apiCallEx: with apiCall, `r` was null on every failure, so `r?.error` was
    // always undefined and this only ever showed the hardcoded "Save failed".
    const r = await apiCallEx(`/settings/config-keys/${key}`, { method: "PUT", body: JSON.stringify({ value }) }, "return");
    setSavingKey(null);
    if (r.ok && r.data?.ok) { flash(key, "Saved. Effective within 60s.", true); load(); }
    else flash(key, errorText(r, "Save failed."), false);
  };

  const clearOverride = async (key: string) => {
    if (!window.confirm(`Remove the stored override for ${key}? The environment fallback applies on next restart.`)) return;
    setSavingKey(key);
    const r = await apiCallEx(`/settings/config-keys/${key}`, { method: "DELETE" }, "return");
    setSavingKey(null);
    if (r.ok && r.data?.ok) { flash(key, "Override removed.", true); load(); }
    else flash(key, errorText(r, "Remove failed."), false);
  };

  const sourceBadge = (st?: SpKeyStatus) => {
    if (!st || st.source === "not_set") return <Badge c={T.txD} b={T.bg4}>Not set</Badge>;
    if (st.source === "database") return <Badge c={T.grn} b={T.grnS}>Saved (database)</Badge>;
    return <Badge c={T.acc} b={T.bg3}>From environment</Badge>;
  };

  if (loading) return <Card style={{ marginBottom: 16, padding: 16, fontSize: 12, color: T.txM }}>Loading SharePoint credentials...</Card>;

  return (
    <Card style={{ marginBottom: 16, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.tx, marginBottom: 4 }}>SharePoint App Credentials</div>
      <div style={{ fontSize: 10, color: T.txD, marginBottom: 12 }}>
        The Microsoft Graph app registration SharePoint authenticates with. Leave a field blank to keep its current value; the secret is write-only and stored encrypted. If unset, the deployment's environment values are used.
      </div>
      {SP_CRED_FIELDS.map(f => {
        const st = statuses[f.key];
        return (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10, flexWrap: "wrap" }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: T.tx }}>{f.label}{f.secret ? " 🔒" : ""}</label>
              {sourceBadge(st)}
            </div>
            <div style={{ fontSize: 10, color: T.txD, marginBottom: 8 }}>{f.help}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type={f.secret ? "password" : "text"}
                value={drafts[f.key] !== undefined ? drafts[f.key] : (f.secret ? "" : (st?.value ?? ""))}
                placeholder={f.secret ? (st?.hasDbValue ? "•••••• (enter to replace)" : "") : ""}
                onChange={e => setDrafts(d => ({ ...d, [f.key]: e.target.value }))}
                autoComplete="off"
                style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
              <Btn v="primary" sz="sm" onClick={() => { save(f.key); }} disabled={savingKey === f.key}>{savingKey === f.key ? "Saving..." : "Save"}</Btn>
              {st?.hasDbValue && <Btn v="secondary" sz="sm" onClick={() => { clearOverride(f.key); }} disabled={savingKey === f.key}>Clear</Btn>}
            </div>
            {fmsg && fmsg.key === f.key && <div style={{ marginTop: 8, fontSize: 11, color: fmsg.ok ? T.grn : T.red }}>{fmsg.text}</div>}
          </div>
        );
      })}
    </Card>
  );
};

export const SharePointSettings = () => {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [drives, setDrives] = useState<any[]>([]);
  const [loadingDrives, setLoadingDrives] = useState(false);
  const [msg, setMsg] = useState("");
  // SHAREPOINT_HOST is a configStore-managed key (PR #37), edited via the generic
  // config-keys API — kept fully separate from the sharepoint_* app_settings the
  // site/library picker below uses. Like all config-keys, GET returns source only
  // (never the stored value), so the input is write-only with a source pill.
  const [host, setHost] = useState("");
  const [hostStatus, setHostStatus] = useState<{ source: string; hasDbValue: boolean } | null>(null);
  const [hostSaving, setHostSaving] = useState(false);
  const [hostMsg, setHostMsg] = useState<{ text: string; ok: boolean } | null>(null);

  useEffect(() => {
    apiCall("/settings").then(d => { setSettings(d.settings || {}); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const loadHostStatus = () => {
    apiCall("/settings/config-keys")
      .then(d => {
        const k = (d?.keys || []).find((x: any) => x.key === "SHAREPOINT_HOST");
        if (k) setHostStatus({ source: k.source, hasDbValue: k.hasDbValue });
      })
      .catch(() => {});
  };
  useEffect(() => { loadHostStatus(); }, []);

  const saveHost = async () => {
    const value = host.trim();
    if (!value) { setHostMsg({ text: "Enter a host first", ok: false }); return; }
    setHostSaving(true); setHostMsg(null);
    const r = await apiCallEx("/settings/config-keys/SHAREPOINT_HOST", { method: "PUT", body: JSON.stringify({ value }) }, "return");
    setHostSaving(false);
    if (r.ok && r.data?.ok) { setHostMsg({ text: "Saved. Effective within 60s.", ok: true }); setHost(""); loadHostStatus(); }
    else setHostMsg({ text: errorText(r, "Save failed."), ok: false });
  };

  const clearHost = async () => {
    if (!window.confirm("Remove the SHAREPOINT_HOST override? The environment/default fallback re-applies on next restart.")) return;
    setHostSaving(true); setHostMsg(null);
    const r = await apiCallEx("/settings/config-keys/SHAREPOINT_HOST", { method: "DELETE" }, "return");
    setHostSaving(false);
    if (r.ok && r.data?.ok) { setHostMsg({ text: "Override removed.", ok: true }); loadHostStatus(); }
    else setHostMsg({ text: errorText(r, "Remove failed."), ok: false });
  };

  const update = (key: string, value: any) => setSettings((p: any) => ({ ...p, [key]: value }));

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      // apiCallEx (not apiCall) so a backend 500/400 carries its REAL message here
      // instead of collapsing to null — which made this screen crash on `res.success`
      // and show a JS TypeError instead of the actual reason (e.g. Graph not configured).
      const { ok, status, data } = await apiCallEx("/settings/sharepoint/test", { method: "POST" });
      if (ok && data?.success) {
        const sites = data.sites || [];
        const note = data.hostConfigured === false ? " (no SharePoint host configured — set one below to look up sites directly)" : "";
        setTestResult({ ok: true, message: `Connected! Found ${sites.length} site(s)${note}` });
        setSites(sites);
      } else {
        setTestResult({ ok: false, message: (data && data.error) || `Connection failed (HTTP ${status})` });
      }
    } catch (err: any) { setTestResult({ ok: false, message: err.message }); }
    setTesting(false);
  };

  const selectSite = async (siteId: string) => {
    const site = sites.find((s: any) => s.id === siteId);
    update("sharepoint_site_id", siteId);
    update("sharepoint_site_name", site?.name || "");
    setLoadingDrives(true);
    try {
      const { ok, data } = await apiCallEx("/settings/sharepoint/drives", { method: "POST", body: JSON.stringify({ site_id: siteId }) });
      setDrives(ok && data ? (data.drives || []) : []);
      if (!ok && data?.error) setTestResult({ ok: false, message: data.error });
    } catch { setDrives([]); }
    setLoadingDrives(false);
  };

  const save = async () => {
    setSaving(true); setMsg("");
    // The old body was `try { await apiCall(...); setMsg("saved successfully") }
    // catch { setMsg("Failed to save") }`. apiCall never throws, so the catch was
    // unreachable and the success message was unconditional — this button
    // reported success even when the settings were rejected. testConnection
    // immediately above was already moved to apiCallEx for this exact reason.
    const r = await apiCallEx("/settings", { method: "PUT", body: JSON.stringify({ settings }) }, "return");
    setSaving(false);
    if (!r.ok) { setMsg(errorText(r, "Settings were not saved.")); return; }
    setMsg("Settings saved successfully");
    setTimeout(() => setMsg(""), 3000);
  };

  if (loading) return <div style={{ padding: 20, color: T.txM, fontSize: 13 }}>Loading settings...</div>;

  return (
    <div>
      <SectionTitle sub="Configure Microsoft SharePoint document library sync">SharePoint Integration</SectionTitle>
      {msg && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: T.grn }}>{msg}</div>}
      <SharePointCredentials />

      <Card style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>SharePoint Host</span>
          {hostStatus && (hostStatus.source === "database"
            ? <Badge c={T.grn} b={T.grnS}>Saved (database)</Badge>
            : hostStatus.source === "env"
              ? <Badge c={T.acc} b={T.bg3}>From environment</Badge>
              : <Badge c={T.txD} b={T.bg4}>Not set</Badge>)}
        </div>
        <div style={{ fontSize: 10, color: T.txD, marginBottom: 8 }}>
          Your own *.sharepoint.com host, used to build document links. Unset means document links are not configured — no host is assumed. You may paste a full site address (for example https://yourcompany.sharepoint.com/sites/Legal) and the site path will be kept with it. Site and library are configured separately below.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="text"
            value={host}
            placeholder={hostStatus?.hasDbValue ? "saved — type to replace" : "yourcompany.sharepoint.com"}
            onChange={e => setHost(e.target.value)}
            style={{ flex: 1, padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
          />
          <Btn v="primary" sz="sm" onClick={() => { saveHost(); }} disabled={hostSaving}>{hostSaving ? "Saving..." : "Save"}</Btn>
          {hostStatus?.hasDbValue && <Btn v="secondary" sz="sm" onClick={() => { clearHost(); }} disabled={hostSaving}>Clear</Btn>}
        </div>
        {hostMsg && <div style={{ marginTop: 8, fontSize: 11, color: hostMsg.ok ? T.grn : T.red }}>{hostMsg.text}</div>}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>SharePoint Sync</span>
          <button onClick={() => update("sharepoint_enabled", settings.sharepoint_enabled === "true" ? "false" : "true")} style={{
            padding: "4px 12px", fontSize: 11, fontWeight: 600, borderRadius: 12, border: "none", cursor: "pointer",
            backgroundColor: settings.sharepoint_enabled === "true" ? T.grn : T.txM,
            color: "#fff"
          }}>{settings.sharepoint_enabled === "true" ? "Enabled" : "Disabled"}</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 8 }}>1. Test Connection</div>
          <Btn v="secondary" sz="sm" onClick={() => { testConnection(); }} disabled={testing}>{testing ? "Testing..." : "Test SharePoint Connection"}</Btn>
          {testResult && <div style={{ marginTop: 8, padding: "6px 12px", borderRadius: 4, fontSize: 12, backgroundColor: testResult.ok ? T.grnS : T.redS, color: testResult.ok ? T.grn : T.red }}>{testResult.message}</div>}
        </div>

        {(sites.length > 0 || testResult) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 8 }}>2. Select SharePoint Site</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {sites.map((s: any) => (
                <button key={s.id} onClick={() => selectSite(s.id)} style={{
                  padding: "8px 12px", textAlign: "left", border: `1px solid ${settings.sharepoint_site_id === s.id ? T.acc : T.brd}`,
                  borderRadius: 6, cursor: "pointer", fontSize: 12,
                  backgroundColor: settings.sharepoint_site_id === s.id ? T.accS : T.bg3,
                  color: T.tx
                }}>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 11, color: T.txM }}>{s.webUrl}</div>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, color: T.txM, marginBottom: 4 }}>Or enter a SharePoint site URL manually:</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input id="spUrlInput" placeholder="https://yourcompany.sharepoint.com/sites/Legal" style={{ flex: 1, padding: "6px 10px", fontSize: 11, borderRadius: 4, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, outline: "none", fontFamily: "inherit" }} />
                <Btn v="secondary" sz="sm" onClick={async () => {
                  const url = (document.getElementById("spUrlInput") as HTMLInputElement).value;
                  if (!url) return;
                  try {
                    const { ok, data } = await apiCallEx("/settings/sharepoint/lookup", { method: "POST", body: JSON.stringify({ site_url: url }) });
                    if (ok && data?.success && data.site) {
                      setSites(prev => { const exists = prev.some((s: any) => s.id === data.site.id); return exists ? prev : [...prev, data.site]; });
                      selectSite(data.site.id);
                    } else {
                      setTestResult({ ok: false, message: (data && data.error) || "Site not found: check the URL" });
                    }
                  } catch (err: any) { setTestResult({ ok: false, message: "Site not found: " + (err.message || "Check URL") }); }
                }}>Look Up</Btn>
              </div>
            </div>
          </div>
        )}

        {drives.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 8 }}>3. Select Document Library</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {drives.map((d: any) => (
                <button key={d.id} onClick={() => { update("sharepoint_drive_id", d.id); update("sharepoint_library_name", d.name); }} style={{
                  padding: "8px 12px", textAlign: "left", border: `1px solid ${settings.sharepoint_drive_id === d.id ? T.acc : T.brd}`,
                  borderRadius: 6, cursor: "pointer", fontSize: 12,
                  backgroundColor: settings.sharepoint_drive_id === d.id ? T.accS : T.bg3,
                  color: T.tx
                }}>
                  <div style={{ fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: T.txM }}>{d.webUrl}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {loadingDrives && <div style={{ fontSize: 12, color: T.txM }}>Loading document libraries...</div>}

        {settings.sharepoint_site_id && settings.sharepoint_drive_id && (
          <div style={{ padding: "10px 14px", backgroundColor: T.bg3, borderRadius: 6, marginBottom: 16, fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: T.tx, marginBottom: 4 }}>Current Configuration</div>
            <div style={{ color: T.txM }}>Site: <span style={{ color: T.tx }}>{settings.sharepoint_site_name || settings.sharepoint_site_id}</span></div>
            <div style={{ color: T.txM }}>Library: <span style={{ color: T.tx }}>{settings.sharepoint_library_name || settings.sharepoint_drive_id}</span></div>
            <div style={{ color: T.txM }}>Status: <span style={{ color: settings.sharepoint_enabled === "true" ? T.grn : T.txM }}>{settings.sharepoint_enabled === "true" ? "Active" : "Inactive"}</span></div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn v="primary" sz="sm" onClick={() => { save(); }} disabled={saving}>{saving ? "Saving..." : "Save Settings"}</Btn>
        </div>
      </Card>
    </div>
  );
};
